import React, { useEffect, useState, ReactNode, useRef, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { User, UserProfile } from 'oidc-client-ts';
import { useApiIntegration } from '../hooks/useApiIntegration';
import { ENV } from '../config/env';
import { AuthContext } from './AuthContextInstance';
import type { AuthContextType, AuthPhase, UserRole } from './AuthContextTypes';
import { getProfile, updateProfile } from '../services/userManagement/userManagement.service';

// Re-export types for convenience
export type { AuthContextType, AuthPhase, UserRole } from './AuthContextTypes';

interface LogoutArgs {
  reason?: 'inactivity';
  logoutParams?: {
    returnTo?: string;
  };
}

// Normalize arbitrary groups claim shapes (array, string, csv, object, null) into a lowercase string[]
const normalizeGroups = (raw: unknown): string[] => {
  if (!raw) {return [];}
  if (Array.isArray(raw)) {return raw.filter(g => typeof g === 'string').map(g => g.toLowerCase());}
  if (typeof raw === 'string') {
    const parts = raw.split(/[;,]/).map(s => s.trim()).filter(Boolean);
    let groupsToMap: string[];
    if (parts.length) {
      groupsToMap = parts;
    } else {
      groupsToMap = [raw];
    }
    return groupsToMap.map(g => g.toLowerCase());
  }
  if (typeof raw === 'object') {
    const vals = Object.values(raw as Record<string, unknown>);
    const strs = vals.filter((v): v is string => typeof v === 'string');
    return strs.map(v => v.toLowerCase());
  }
  return [];
};

// Parse env group setting into list (supports comma or semicolon separated values)
const parseGroupSetting = (value: string): string[] => value
  .split(/[;,]/)
  .map(s => s.trim().toLowerCase())
  .filter(Boolean);

// Map user groups to application roles (supports multiple admin/user group names)
const mapUserRole = (
  user: User,
  adminGroups: string[],
  userGroups: string[]
): UserRole => {
  const rawGroups = (user.profile as Record<string, unknown>).groups;
  const groups = normalizeGroups(rawGroups); // already lowercased
  let role: 'Admin' | 'Support' | 'NormalUser' | 'NotAssigned';

  const isAdmin = adminGroups.some(g => groups.includes(g));
  const isUser = userGroups.some(g => groups.includes(g));

  if (isAdmin) {
    role = 'Admin';
  } else if (isUser) {
    role = 'NormalUser';
  } else {
    throw new Error("User does not belong to any recognized group");
  }

  return { role, groups };
};

// Compute env based group lists once (prevent new array identity each render triggering effects)
const ADMIN_GROUPS = parseGroupSetting(ENV.ADMIN_GROUP);
const USER_GROUPS = parseGroupSetting(ENV.USER_GROUP);

const INVALID_REFRESH_TOKEN_MARKERS = [
  'invalid refresh token',
  'expired refresh token',
  'unknown, invalid, or expired refresh token'
];

const extractErrorText = (error: unknown): string => {
  if (!error) {return '';}
  if (typeof error === 'string') {return error;}
  if (error instanceof Error) {return `${error.name} ${error.message}`;}
  try {
    const serialized = JSON.stringify(error);
    if (serialized) {
      return serialized;
    }
  } catch {
    // Ignore serialization failures and use fallback text.
  }
  return 'unknown auth error';
};

const isInvalidRefreshTokenError = (error: unknown): boolean => {
  const text = extractErrorText(error).toLowerCase();
  return INVALID_REFRESH_TOKEN_MARKERS.some(marker => text.includes(marker));
};

export const AuthContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userProfile, setUserProfile] = useState<AuthContextType['userProfile']>(null);
  const lastRoleRef = useRef<string | null>(null);
  const lastGroupsRef = useRef<string>('');
  // Track whether the current user is exempt from session timeout (specific accounts)
  const noTimeoutUserRef = useRef(false);
  const [authPhase, setAuthPhase] = useState<AuthPhase>('initializing');
  const lastActivityRef = useRef<number>(Date.now());
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [warningActive, setWarningActive] = useState(false);
  const sessionIntervalRef = useRef<number | null>(null);
  const fireAtRef = useRef<number | null>(null);
  const warnAtRef = useRef<number | null>(null);
  const SESSION_TIMEOUT = 30;

  // Inactivity / session timeout configuration
  const timeoutMinutes = ENV.SESSION_TIMEOUT_MINUTES || SESSION_TIMEOUT;
  const warningLeadSeconds = ENV.SESSION_TIMEOUT_WARNING_SECONDS || 0;

  const clearSessionInterval = () => {
    if (sessionIntervalRef.current) {
      globalThis.clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = null;
    }
  };

  const startSessionTimers = () => {
    clearSessionInterval();
    setTimedOut(false);
    setRemainingSeconds(null);
    setWarningActive(false);
    // Certain user accounts are exempt from session timeout
    if (noTimeoutUserRef.current) {return;}
    if (!timeoutMinutes || timeoutMinutes <= 0 || !auth.isAuthenticated) {return;}

    const now = Date.now();
    const totalMs = timeoutMinutes * 60 * 1000;
    const warnMs = Math.max(0, warningLeadSeconds * 1000);
    fireAtRef.current = now + totalMs;
    if (warnMs > 0) {
      warnAtRef.current = fireAtRef.current - warnMs;
    } else {
      warnAtRef.current = null;
    }

    const tick = async () => {
      if (!fireAtRef.current) {return;}
      const current = Date.now();
      const timeToExpiry = Math.ceil((fireAtRef.current - current) / 1000);
      const shouldBeWarning = !!(warnAtRef.current && current >= warnAtRef.current);

      if (shouldBeWarning && !warningActive) {setWarningActive(true);}

      if (warningActive || shouldBeWarning) {setRemainingSeconds(Math.max(0, timeToExpiry));}

      if (current >= fireAtRef.current) {
        clearSessionInterval();
        try {
          setAuthPhase('signingOut');
          // Do not clear storage here — allow the OIDC client to revoke tokens.
          await logout({
            reason: 'inactivity',
            logoutParams: { returnTo: ENV.OIDC_POST_LOGOUT_REDIRECT_URI || `${globalThis.location.origin}/login` }
          });
        } catch (e) {
          console.info('Auto logout (inactivity) failed, performing local cleanup:', e);
          setUserRole(null);
          setUserProfile(null);
          lastRoleRef.current = null;
          setTimedOut(true);
          // Best-effort local cleanup if remote signout fails
          try {
            globalThis.localStorage.clear();
            globalThis.sessionStorage.clear();
          } catch (clearErr) {
            console.info('Failed clearing storage during auto-logout fallback:', clearErr);
          }
          globalThis.location.href = '/login';
        }
      }
    };

    // Run immediately then every second
    tick();
    sessionIntervalRef.current = globalThis.setInterval(tick, 1000) as unknown as number;
  };

  const recordActivity = () => {
    const now = Date.now();
    if (now - lastActivityRef.current < 1000 && !warningActive) {return;}
    lastActivityRef.current = now;
    startSessionTimers();
  };

  // User activity listeners
  useEffect(() => {
    if (!auth.isAuthenticated || !timeoutMinutes || timeoutMinutes <= 0) {
      clearSessionInterval();
      return () => {
        clearSessionInterval();
      };
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'] as const;
    for (const ev of events) {
      globalThis.addEventListener(ev, recordActivity as EventListener, { passive: true } as AddEventListenerOptions);
    }
    startSessionTimers();
    return () => {
      for (const ev of events) {
        globalThis.removeEventListener(ev, recordActivity as EventListener);
      }
      clearSessionInterval();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, timeoutMinutes]);
  
  // Initialize API integration
  useApiIntegration();

  // Debug logging for OIDC state
  useEffect(() => {
    // Auth state updated (debug logging removed)

    const forceLoginAfterAuthCleanup = async () => {
      try {
        await auth.removeUser();
      } catch (removeError) {
        console.info('[Auth] Failed to remove stale OIDC user:', removeError);
      }
      try {
        localStorage.removeItem(`oidc.user:${ENV.OIDC_AUTHORITY}:${ENV.OIDC_CLIENT_ID}`);
        sessionStorage.clear();
      } catch (storageError) {
        console.info('[Auth] Failed to clear storage:', storageError);
      }
      if (!globalThis.location.pathname.includes('/login')) {
        globalThis.location.href = '/login';
      }
    };

    const shouldForceInteractiveLogin = auth.error?.message?.includes('No matching state found in storage')
      || isInvalidRefreshTokenError(auth.error);

    // Auth state cannot be recovered silently; force full login flow.
    if (shouldForceInteractiveLogin) {
      console.info('[Auth] Auth session state is stale. Forcing interactive login.');
      void forceLoginAfterAuthCleanup();
      return;
    }

    if (auth.isLoading) {
      if (auth.activeNavigator === 'signinSilent') {
        setAuthPhase(prev => {
          if (prev === 'signingIn') {
            return prev;
          } else {
            return 'refreshing';
          }
        });
      } else if (authPhase === 'idle') {
        setAuthPhase('initializing');
      } else {
        // Explicit else clause to handle all other cases
        // No action needed, but required for exhaustive handling
      }
    } else {
      setAuthPhase(prev => {
        const isTransitioning = prev === 'signingIn' || prev === 'signingOut' || prev === 'refreshing' || prev === 'initializing';
        if (isTransitioning) {
          return 'idle';
        } else {
          return prev;
        }
      });
    }

    if (!auth.isLoading && !auth.isAuthenticated && !globalThis.location.pathname.includes('/login') && !globalThis.location.pathname.includes('/callback')) {
      globalThis.location.href = '/login';
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.error, auth.user, auth.activeNavigator]);

  // Environment variables for role mapping (stable, module-level constants)
  const adminGroups = ADMIN_GROUPS;
  const userGroups = USER_GROUPS;

  // Extract user profile information
  const extractUserProfile = (user: User): AuthContextType['userProfile'] => {
    const profile = user.profile as UserProfile | Record<string, unknown>;
    return {
      name: (() => {
        if (typeof profile.name === 'string' && profile.name) {
          return profile.name;
        }
        let firstName = '';
        let lastName = '';
        if (typeof profile.firstName === 'string') {
          firstName = profile.firstName;
        }
        if (typeof profile.lastName === 'string') {
          lastName = profile.lastName;
        }
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) {
          return fullName;
        }
        if (typeof profile.preferred_username === 'string' && profile.preferred_username) {
          return profile.preferred_username;
        }
        return 'Unknown User';
      })(),
      email: (() => {
        if (typeof profile.email === 'string') {
          return profile.email;
        } else {
          return undefined;
        }
      })(),
      sub: (() => {
        if (typeof profile.sub === 'string') {
          return profile.sub;
        } else {
          return undefined;
        }
      })(),
      preferred_username: (() => {
        if (typeof profile.preferred_username === 'string') {
          return profile.preferred_username;
        } else {
          return undefined;
        }
      })(),
      firstName: (() => {
        if (typeof profile.firstName === 'string') {
          return profile.firstName;
        } else {
          return undefined;
        }
      })(),
      lastName: (() => {
        if (typeof profile.lastName === 'string') {
          return profile.lastName;
        } else {
          return undefined;
        }
      })(),
      title: (() => {
        if (typeof profile.title === 'string') {
          return profile.title;
        } else {
          return undefined;
        }
      })(),
      company: (() => {
        if (typeof profile.company === 'string') {
          return profile.company;
        } else {
          return undefined;
        }
      })(),
      division: (() => {
        if (typeof profile.division === 'string') {
          return profile.division;
        } else {
          return undefined;
        }
      })(),
      employeeID: (() => {
        if (typeof profile.employeeID === 'string') {
          return profile.employeeID;
        } else {
          return undefined;
        }
      })(),
      employeeNumber: (() => {
        if (typeof profile.employeeNumber === 'string') {
          return profile.employeeNumber;
        } else {
          return undefined;
        }
      })(),
      manager: (() => {
        if (typeof profile.manager === 'string') {
          return profile.manager;
        } else {
          return undefined;
        }
      })(),
      managerName: (() => {
        if (typeof profile.managerName === 'string') {
          return profile.managerName;
        } else {
          return undefined;
        }
      })(),
    };
  };

  // Helper to extract OIDC profile fields
  interface OidcProfileFields {
    oidcFirstName: string;
    oidcLastName: string;
    oidcEmail: string;
  }
  const getOidcProfileFields = (profile: UserProfile | Record<string, unknown>): OidcProfileFields => {
    let oidcFirstName = '';
    let oidcLastName = '';
    let oidcEmail = '';
    if (typeof profile.firstName === 'string') {
      oidcFirstName = profile.firstName;
    } else if (typeof profile.given_name === 'string') {
      oidcFirstName = profile.given_name;
    } else {
      // No action needed
    }
    if (typeof profile.lastName === 'string') {
      oidcLastName = profile.lastName;
    } else if (typeof profile.family_name === 'string') {
      oidcLastName = profile.family_name;
    } else {
      // No action needed
    }
    if (typeof profile.email === 'string') {
      oidcEmail = profile.email;
    } else {
      // No action needed
    }
    return { oidcFirstName, oidcLastName, oidcEmail };
  };

  // Helper to update auto-provisioned profile
  interface ApiProfileData {
    firstName: string;
    lastName: string;
    email?: string;
  }
  const updateAutoProvisionedProfile = async (
    apiData: { firstName: string; lastName: string; email?: string },
    oidcFields: OidcProfileFields
  ) => {
    const { oidcFirstName, oidcLastName, oidcEmail } = oidcFields;
    if (!(apiData.firstName === 'Auto' && apiData.lastName === 'Provisioned')) {
      return;
    }
    if (!(oidcFirstName || oidcLastName || oidcEmail)) {
      return;
    }
    const updatePayload: Record<string, string> = {};
    if (oidcFirstName && oidcFirstName !== apiData.firstName) {
      updatePayload.firstName = oidcFirstName;
    }
    if (oidcLastName && oidcLastName !== apiData.lastName) {
      updatePayload.lastName = oidcLastName;
    }
    if (oidcEmail && oidcEmail !== apiData.email) {
      updatePayload.email = oidcEmail;
    }
    if (Object.keys(updatePayload).length === 0) {
      return;
    }
    try {
      const updateResponse = await updateProfile(updatePayload);
      if (!updateResponse.success) {
        console.info('[Auth] Failed to update profile:', updateResponse.error);
      }
    } catch (updateError) {
      console.info('[Auth] Error updating profile:', updateError);
    }
  };

  // Helper to map API role
  const mapApiRole = (apiRole: string | number): 'Admin' | 'Support' | 'NormalUser' | 'NotAssigned' => {
    const ROLE_ADMIN = 1;
    const ROLE_SUPPORT = 2;
    const ROLE_NORMAL = 3;
    if (typeof apiRole === 'string') {
      const roleLower = apiRole.toLowerCase();
      if (roleLower === 'admin') {
        return 'Admin';
      } else if (roleLower === 'support') {
        return 'Support';
      } else if (roleLower === 'normal' || roleLower === 'normaluser') {
        return 'NormalUser';
      } else {
        return 'NotAssigned';
      }
    } else if (typeof apiRole === 'number') {
      if (apiRole === ROLE_ADMIN) {
        return 'Admin';
      } else if (apiRole === ROLE_SUPPORT) {
        return 'Support';
      } else if (apiRole === ROLE_NORMAL) {
        return 'NormalUser';
      } else {
        return 'NotAssigned';
      }
    } else {
      return 'NotAssigned';
    }
  };

  // Refactored effect for user role/profile update
  useEffect(() => {
    const updateUserState = (role: UserRole, profile: AuthContextType['userProfile']) => {
      const groupsKey = role.groups.join('|');
      const roleChanged = lastRoleRef.current !== role.role;
      const groupsChanged = lastGroupsRef.current !== groupsKey;
      if (roleChanged || groupsChanged) {
        lastRoleRef.current = role.role;
        lastGroupsRef.current = groupsKey;
        setUserRole(role);
        setUserProfile(profile);
      }
    };

    const fallbackToOidc = () => {
      if (auth.user) {
        const role = mapUserRole(auth.user, adminGroups, userGroups);
        const profile = extractUserProfile(auth.user);
        noTimeoutUserRef.current = (() => {
          const p = auth.user.profile as Record<string, unknown>;
          // preferred_username from Ping is the NT/employee ID (e.g. SESI009724)
          const pref = p.preferred_username;
          const email = p.email;
          return (typeof pref === 'string' && pref.toUpperCase() === 'SESI009724')
            || (typeof email === 'string' && email === 'globalcsh.wallboard02@se.com');
        })();
        // Re-run timer logic so an exempt user's already-running interval gets cancelled
        if (noTimeoutUserRef.current) { startSessionTimers(); }
        updateUserState(role, profile);
      }
    };

    if (auth.user && auth.isAuthenticated) {
      (async () => {
        try {
          const apiProfileResponse = await getProfile();
          if (apiProfileResponse.success && apiProfileResponse.data) {
            const apiData = apiProfileResponse.data;
            const apiRole = apiData.role;
            const currentUser = auth.user;
            if (!currentUser) { return; }
            const oidcProfile = currentUser.profile as UserProfile | Record<string, unknown>;
            const oidcFields = getOidcProfileFields(oidcProfile);
            await updateAutoProvisionedProfile(
              { firstName: apiData.firstName ?? '', lastName: apiData.lastName ?? '', email: apiData.email },
              oidcFields
            );
            // apiRole may be undefined, so fallback to 'NotAssigned' if so
            const mappedRole = mapApiRole(apiRole ?? 'NotAssigned');
            // Use API profile data to identify timeout-exempt users (reliable, server-sourced)
            noTimeoutUserRef.current = apiData.email === 'globalcsh.wallboard02@se.com'
              || apiData.employeeId === 'SESI009724';
            // Re-run timer logic so an exempt user's already-running interval gets cancelled
            if (noTimeoutUserRef.current) { startSessionTimers(); }
            const role: UserRole = { role: mappedRole, groups: [] };
            const profile = extractUserProfile(currentUser);
            updateUserState(role, profile);
          } else {
            fallbackToOidc();
          }
        } catch (error) {
          console.info('[Auth] Failed to fetch API profile, falling back to OIDC groups:', error);
          fallbackToOidc();
        }
      })();
    } else if (!auth.isAuthenticated && (userRole !== null || userProfile !== null)) {
      lastRoleRef.current = null;
      lastGroupsRef.current = '';
      setUserRole(null);
      setUserProfile(null);
    } else {
      // No action needed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, auth.isAuthenticated]);

  // Authentication methods
  const loginWithRedirect = async (args?: Record<string, unknown>) => {
    // Ignore repeated clicks while a redirect is already in progress.
    if (authPhase === 'signingIn') {return;}
    try {
      setAuthPhase('signingIn');
      await auth.signinRedirect({
        ...args,
  scope: ENV.OIDC_SCOPE,
      });
    } catch (error) {
      console.info('Login failed:', error);
      setAuthPhase('idle');
      throw error;
    }
  };

  const logout = async (args?: LogoutArgs) => {
    try {
      setAuthPhase('signingOut');

      // Clear local state first
      setUserRole(null);
      setUserProfile(null);
      lastRoleRef.current = null;
      lastGroupsRef.current = '';

      // Determine redirect URI
      const redirectUri = args?.logoutParams?.returnTo || ENV.OIDC_POST_LOGOUT_REDIRECT_URI || `${globalThis.location.origin}/login`;

      try {
        // First, attempt to revoke tokens using the OIDC client if supported.
        try {
          const maybeRevoke = (auth as unknown as Record<string, unknown>).revokeTokens || (auth as unknown as Record<string, unknown>).revoke;
          if (typeof maybeRevoke === 'function') {
            await maybeRevoke.call(auth);
          }
        } catch (revErr) {
          console.info('Token revocation attempt failed (continuing to signout):', revErr);
        }

        // Attempt OIDC signout with redirect. The OIDC client will remove user from storage
        await auth.signoutRedirect({ 
          post_logout_redirect_uri: redirectUri,
          // Some IdPs require explicit state parameter
          state: { returnTo: redirectUri }
        });

        // Set a timeout fallback in case the identity provider doesn't redirect
        setTimeout(async () => {
          console.info('Logout redirect timeout - forcing local cleanup and redirect');
          try {
            // Ensure local cleanup if signout redirect didn't complete
            await auth.removeUser();
          } catch (remErr) {
            console.info('Failed to remove user during logout fallback:', remErr);
          }
          try {
            globalThis.localStorage.clear();
            globalThis.sessionStorage.clear();
          } catch (clearErr) {
            console.info('Failed clearing storage during logout fallback:', clearErr);
          }
          globalThis.location.href = redirectUri;
        }, 1000);
      } catch (signoutError) {
        console.info('Remote signout failed, performing local cleanup:', signoutError);
        if (args?.reason === 'inactivity') {setTimedOut(true);}
        // Fallback: attempt to revoke/remove locally then redirect
        try {
          const maybeRevoke = (auth as unknown as Record<string, unknown>).revokeTokens || (auth as unknown as Record<string, unknown>).revoke;
          if (typeof maybeRevoke === 'function') {
            await maybeRevoke.call(auth);
          }
        } catch (_) {
           console.info('Token revocation attempt failed during logout fallback', _);
        }
        try {
          await auth.removeUser();
        } catch (remErr) {
          console.info('Failed to remove user during logout fallback:', remErr);
        }
        try {
          globalThis.localStorage.clear();
          globalThis.sessionStorage.clear();
        } catch (clearErr) {
          console.info('Failed clearing storage during logout fallback:', clearErr);
        }
        // Fallback to local redirect
        globalThis.location.href = redirectUri;
      }
    } catch (err) {
      console.info('Logout failed:', err);
      // Last resort: force redirect to login
      globalThis.location.href = '/login';
    }
  };

  // Role checking methods
  const hasRole = (role: string): boolean => userRole?.role === role;
  const hasAnyRole = (roles: string[]): boolean => roles.some(role => hasRole(role));
  // Support users get full admin-level access plus no session timeout
  const isAdmin = (): boolean => hasRole('Admin') || hasRole('Support');
  const isSupport = (): boolean => hasRole('Support');
  const isNormalUser = (): boolean => hasRole('NormalUser');

  const contextValue: AuthContextType = useMemo(() => ({
    authPhase,
    userProfile,
    userRole,
    loginWithRedirect,
    logout,
    hasRole,
    hasAnyRole,
    isAdmin,
    isSupport,
    isNormalUser,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user ?? null,
    error: auth.error ?? null,
    session: {
      timeoutMinutes,
      warningLeadSeconds,
      remainingSeconds,
      willAutoLogout: !!(timeoutMinutes && timeoutMinutes > 0 && auth.isAuthenticated && !noTimeoutUserRef.current),
  timedOut,
  warningActive,
      resetActivity: recordActivity
    }
  }), [auth.isAuthenticated, auth.isLoading, auth.user, auth.error, authPhase, timeoutMinutes, warningLeadSeconds, remainingSeconds, timedOut, warningActive, userProfile, userRole]);
     
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
